/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {useTranslation} from 'react-i18next'
import React from 'react'
import UiFilterLabel from './Filter/UiFilterLabel'
import clsx from 'clsx'
import {useDebouncedCallback} from 'use-debounce'
import {ChevronDownIcon} from '@heroicons/react/16/solid'
import {UiTabVariant} from 'types/enums/UiTabVariant'

export interface UiTabSwitcherItem {
    id: number | string
    name: string
    count?: string | number
    disabled?: boolean
    tooltipId?: string
    tooltipContent?: string
    onMouseEnter?: () => void
    onMouseLeave?: () => void
	className?: string
}

interface UiTabSwitcherProps {
    selected: string | number
    label?: string
    onClick: (tab: string | number) => void
    items: UiTabSwitcherItem[]
    disabled?: boolean
    className?: string
    variant?: UiTabVariant 
}

const UiTabSwitcher = (props: UiTabSwitcherProps) => {
    const {t} = useTranslation()
    const debouncedChangeHandler = useDebouncedCallback(
        (data: string | number) => props.onClick(data),
        300
    )

    const onChangeHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
        debouncedChangeHandler(e.target.value)
    }

    const onClickHandler = (id: number | string) => {
        debouncedChangeHandler(id)
    }

    const variant = props.variant || UiTabVariant.Default

    return (
        <div
            className={clsx(
                'UiTabSwitcher flex flex-col w-auto h-full',
                props.className
            )}
        >
            {props.label && <UiFilterLabel label={props.label} />}

            <div className="grid grid-cols-1 sm:hidden items-center h-full">
                <select
                    value={props.selected}
                    onChange={onChangeHandler}
                    disabled={props.disabled}
                    aria-label={t('Select a tab')}
                    className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 dark:bg-white/5 dark:text-gray-100 dark:outline-white/10 dark:focus:outline-indigo-500"
                >
                    {props.items.map((item) => (
                        <option key={item.id} value={item.id} disabled={props.disabled || item.disabled}>
                            {item.name}
                        </option>
                    ))}
                </select>
                <ChevronDownIcon
                    aria-hidden="true"
                    className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-400"
                />
            </div>

            <div className="hidden sm:block h-full">
                <div
                    className={clsx(
                        'h-full flex items-center',
                        variant === UiTabVariant.Underline &&
                            'border-b border-gray-200 dark:border-white/10'
                    )}
                >
                    <nav
                        aria-label={t('Tabs')}
                        className={clsx(
                            variant === UiTabVariant.Underline && '-mb-px flex py-2 space-x-8 h-full',
                            variant === UiTabVariant.Default && 'flex space-x-4',
                            variant === UiTabVariant.Group &&
                                'flex isolate rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden'
                        )}
                    >
                        {props.items.map((item, index) => {
                            const isSelected =
                                String(item.id) === String(props.selected)
                            const isDisabled = props.disabled || item.disabled

                            const wrapperProps =
                                item.onMouseEnter ||
                                item.onMouseLeave ||
                                item.tooltipId ||
                                item.tooltipContent
                                    ? {
                                          onMouseEnter: item.onMouseEnter,
                                          onMouseLeave: item.onMouseLeave,
                                          'data-tooltip-id': item.tooltipId,
                                          'data-tooltip-content':
                                              item.tooltipContent,
                                      }
                                    : undefined

                            if (variant === UiTabVariant.Group) {
                                const button = (
                                    <button
                                        key={item.id}
                                        type="button"
                                        onClick={() => onClickHandler(item.id)}
                                        disabled={isDisabled}
                                        className={clsx(
											"UiTabVariant.Group",
											item.className,
                                            'relative inline-flex items-center px-4 py-2 text-sm font-semibold transition-all duration-200',
                                            index > 0 &&
                                                'border-l border-gray-200 dark:border-gray-800',
                                            isSelected
                                                ? 'bg-cta-light text-white z-10 shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)]'
                                                : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800',
                                            isDisabled &&
                                                'opacity-50 cursor-not-allowed',
                                        )}
                                    >
                                        {item.name}
                                        {item.count !== undefined && (
                                            <span
                                                className={clsx(
                                                    'ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold',
                                                    isSelected
                                                        ? 'bg-white/20 text-white'
                                                        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                                )}
                                            >
                                                {item.count}
                                            </span>
                                        )}
                                    </button>
                                )

                                if (!wrapperProps) {
                                    return button
                                }

                                return (
                                    <span
                                        key={item.id}
                                        className="inline-flex"
                                        {...wrapperProps}
                                    >
                                        {button}
                                    </span>
                                )
                            }

                            if (variant === UiTabVariant.Default) {
                                const button = (
                                    <button
                                        key={item.id}
                                        type="button"
                                        onClick={() => onClickHandler(item.id)}
                                        disabled={isDisabled || isSelected}
                                        className={clsx(
											"UiTabVariant.Default",
											item.className,
											'rounded-md px-3 py-2 text-sm font-medium transition-all duration-200 cursor-pointer',
                                            isSelected
                                                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300'
                                                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
                                            isDisabled &&
                                                'opacity-50 pointer-events-none cursor-not-allowed'
                                        )}
                                    >
                                        {item.name}
                                        {item.count !== undefined && (
                                            <span
                                                className={clsx(
                                                    'ml-2.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
                                                    isSelected
                                                        ? 'bg-indigo-200 text-indigo-800 dark:bg-indigo-400/20 dark:text-indigo-200'
                                                        : 'bg-gray-100 text-gray-900 dark:bg-white/10 dark:text-gray-300'
                                                )}
                                            >
                                                {item.count}
                                            </span>
                                        )}
                                    </button>
                                )

                                if (!wrapperProps) {
                                    return button
                                }

                                return (
                                    <span key={item.id} className="inline-flex" {...wrapperProps}>
                                        {button}
                                    </span>
                                )
                            }

                            const button = (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => onClickHandler(item.id)}
                                    disabled={isDisabled}
                                    aria-current={
                                        isSelected ? 'page' : undefined
                                    }
                                    className={clsx(
										"UiTabVariant.Normal",
										item.className,
                                        isSelected
                                            ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-white/20 dark:hover:text-white',
                                        'flex border-b-2 px-2 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors duration-200 items-center outline-none h-full',
                                        isDisabled && 'opacity-50 pointer-events-none cursor-not-allowed'
                                    )}
                                >
                                    {item.name}
                                    {item.count !== undefined && (
                                        <span
                                            className={clsx(
                                                isSelected
                                                    ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400'
                                                    : 'bg-gray-100 text-gray-900 dark:bg-white/10 dark:text-gray-300',
                                                'ml-3 hidden rounded-full px-2.5 py-0.5 text-xs font-medium md:inline-block'
                                            )}
                                        >
                                            {item.count}
                                        </span>
                                    )}
                                </button>
                            )

                            if (!wrapperProps) {
                                return button
                            }

                            return (
                                <span key={item.id} className="inline-flex" {...wrapperProps}>
                                    {button}
                                </span>
                            )
                        })}
                    </nav>
                </div>
            </div>
        </div>
    )
}

export default UiTabSwitcher
